import React from "react"
import { Link } from "react-router-dom"
import { ArrowDown, ArrowRight, Check, Info } from "lucide-react"
import SEO from "../../components/SEO.jsx"
import PageHero from "../../components/page/PageHero.jsx"
import RelatedServices from "../../components/page/RelatedServices.jsx"
import PillarsSection from "../../components/page/PillarsSection.jsx"
import FeatureSection from "../../components/page/FeatureSection.jsx"
import SectionHeading from "../../components/home/SectionHeading.jsx"
import FinalCTA from "../../components/home/FinalCTA.jsx"
import { getService } from "../../data/services.js"
import useHashScroll from "../../hooks/useHashScroll.js"
import { buildServiceSchema } from "./serviceSchema.js"

// Data-driven service page. Content lives in data/services.js (`page`).
export default function ServicePage({ serviceKey }) {
  const service = getService(serviceKey)
  useHashScroll()
  if (!service?.page) return null

  const { page } = service

  return (
    <div className="bg-white text-brand-text">
      <SEO
        title={page.seoTitle}
        description={page.seoDescription}
        path={service.path}
        image="/og-image.png"
        schema={buildServiceSchema(service)}
      />

      <PageHero eyebrow={service.title} title={page.heroHeading} lead={page.heroLead} service={service.key}>
        {page.heroLink && (
          <Link
            to={page.heroLink.to}
            className="mt-6 inline-flex items-center gap-2 font-semibold text-white underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {page.heroLink.label}
            <ArrowDown aria-hidden="true" className="h-4 w-4" />
          </Link>
        )}
      </PageHero>

      {page.pillars && <PillarsSection {...page.pillars} />}

      <section aria-labelledby="challenges-heading" className="py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionHeading id="challenges-heading" title={page.challengesHeading} />
            <ul className="mt-6 space-y-4">
              {page.challenges.map((item) => (
                <li key={item} className="flex gap-3 text-brand-text">
                  <Check aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-brand-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold tracking-tight text-brand-heading sm:text-3xl">
              {page.includedHeading}
            </h2>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2">
              {page.included.map((item) => (
                <li
                  key={item.title}
                  className="rounded-xl2 border border-brand-border bg-white p-5 shadow-card"
                >
                  <h3 className="font-semibold text-brand-heading">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-brand-textSoft">{item.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {page.feature && <FeatureSection {...page.feature} />}

      <section aria-labelledby="process-heading" className="bg-brand-bg py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <SectionHeading id="process-heading" eyebrow="How we work" title="A clear, practical process" />
          <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {page.process.map((step, index) => (
              <li key={step.title} className="rounded-xl2 border border-brand-border bg-white p-6 shadow-card">
                <p className="text-sm font-semibold text-brand-primary">Step {index + 1}</p>
                <h3 className="mt-2 font-semibold text-brand-heading">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-textSoft">{step.text}</p>
              </li>
            ))}
          </ol>

          {page.note && (
            <p className="mt-8 flex max-w-3xl gap-3 rounded-xl2 border border-brand-border bg-white p-5 text-sm leading-relaxed text-brand-textSoft">
              <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary" />
              {page.note}
            </p>
          )}
        </div>
      </section>

      {page.related?.length > 0 && (
        <section aria-labelledby="related-topics-heading" className="py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h2
              id="related-topics-heading"
              className="text-2xl font-bold tracking-tight text-brand-heading"
            >
              Common problems we help with
            </h2>
            <ul className="mt-6 flex flex-wrap gap-3">
              {page.related.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-brand-border px-4 py-2 text-sm font-medium text-brand-heading transition hover:border-brand-primary hover:text-brand-primary"
                  >
                    {link.label}
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <RelatedServices exclude={service.key} />
      <FinalCTA service={service.key} />
    </div>
  )
}
